import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, ExternalLink, Sparkles } from "lucide-react";
import { useSite } from "../context/SiteContext";
import {
  PEOPLE,
  CardFront,
  CardBack,
  MidnightCardFront,
  MidnightCardBack,
  OnyxCardFront,
  OnyxCardBack,
  ForestCardFront,
  ForestCardBack,
  ImprintCardFront,
  ImprintCardBack,
} from "./Marketing";
import {
  THEMES,
  ThemedLetterhead,
  ThemedSocialPost,
  ThemedPostcardFront,
  ThemedPostcardBack,
  ThemedWindowDecal,
  ThemedFlyer,
} from "../components/marketing-themes";

// Render the right card components for a given theme.
function CardForTheme({ themeKey, person, brand, tagline, logoUrl, phone, email, addr1, addr2 }) {
  switch (themeKey) {
    case "sage":
      return (
        <>
          <CardFront
            person={person} brand={brand} tagline={tagline}
            logoUrl={logoUrl} phone={phone} email={email} addr1={addr1} addr2={addr2}
          />
          <div className="mt-3" />
          <CardBack person={person} brand={brand} />
        </>
      );
    case "midnight":
      return (
        <>
          <MidnightCardFront brand={brand} logoUrl={logoUrl} />
          <div className="mt-3" />
          <MidnightCardBack person={person} brand={brand} phone={phone} email={email} addr1={addr1} addr2={addr2} />
        </>
      );
    case "onyx":
      return (
        <>
          <OnyxCardFront brand={brand} logoUrl={logoUrl} />
          <div className="mt-3" />
          <OnyxCardBack person={person} brand={brand} phone={phone} email={email} addr1={addr1} addr2={addr2} />
        </>
      );
    case "forest":
      return (
        <>
          <ForestCardFront brand={brand} logoUrl={logoUrl} />
          <div className="mt-3" />
          <ForestCardBack person={person} brand={brand} phone={phone} email={email} addr1={addr1} addr2={addr2} />
        </>
      );
    case "imprint":
      return (
        <>
          <ImprintCardFront person={person} brand={brand} logoUrl={logoUrl} />
          <div className="mt-3" />
          <ImprintCardBack person={person} brand={brand} phone={phone} email={email} addr1={addr1} addr2={addr2} logoUrl={logoUrl} />
        </>
      );
    default:
      return null;
  }
}

// One full brand-world section (cards + letterhead + social + postcard) for a theme.
function ThemeSection({ themeKey, brand, tagline, logoUrl, phone, email, addr1, addr2, services }) {
  const t = THEMES[themeKey];
  return (
    <section id={t.key} className="mb-28" data-testid={`theme-${t.key}`}>
      <header className="border-b border-[#E2DCD0] pb-6 mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="overline text-[#9F5C45] mb-2">The {t.name} world</p>
          <h2 className="font-serif font-light text-6xl md:text-7xl text-[#1C1917] tracking-tight">
            {t.name}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-12 h-12 border border-[#E2DCD0]" style={{ background: t.surfaceGradient || t.surface }} title="Surface" />
          <span className="w-12 h-12 border border-[#E2DCD0]" style={{ background: t.accent }} title="Accent" />
        </div>
      </header>

      {/* Business cards row */}
      <div className="mb-14">
        <p className="overline text-[#57534E] mb-5">Business card · front + back</p>
        <div className="flex flex-wrap items-start gap-12">
          {PEOPLE.map((person) => (
            <div key={person.key} className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#9F5C45]">{person.name}</p>
              <div className="flex flex-col items-start">
                <CardForTheme
                  themeKey={t.key}
                  person={person}
                  brand={brand}
                  tagline={tagline}
                  logoUrl={logoUrl}
                  phone={phone}
                  email={email}
                  addr1={addr1}
                  addr2={addr2}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Letterhead + Flyer (full pages) */}
      <div className="grid lg:grid-cols-2 gap-10 mb-14">
        <div>
          <p className="overline text-[#57534E] mb-4">Letterhead · 8.5″ × 11″</p>
          <div
            className="origin-top-left border border-[#E2DCD0]"
            style={{ transform: "scale(0.36)", transformOrigin: "top left", width: "8.5in", height: "11in", marginBottom: "-7in", marginRight: "-5.4in" }}
          >
            <ThemedLetterhead theme={t} brand={brand} logoUrl={logoUrl} phone={phone} email={email} addr1={addr1} addr2={addr2} />
          </div>
        </div>
        <div>
          <p className="overline text-[#57534E] mb-4">Flyer · 8.5″ × 11″</p>
          <div
            className="origin-top-left border border-[#E2DCD0]"
            style={{ transform: "scale(0.36)", transformOrigin: "top left", width: "8.5in", height: "11in", marginBottom: "-7in", marginRight: "-5.4in" }}
          >
            <ThemedFlyer theme={t} brand={brand} logoUrl={logoUrl} phone={phone} email={email} addr1={addr1} addr2={addr2} services={services} />
          </div>
        </div>
      </div>

      {/* Postcard front + back */}
      <div className="grid md:grid-cols-2 gap-10 mb-14">
        <div>
          <p className="overline text-[#57534E] mb-4">Postcard · front · 6″ × 4″</p>
          <div
            className="origin-top-left border border-[#E2DCD0]"
            style={{ transform: "scale(0.55)", transformOrigin: "top left", width: "6in", height: "4in", marginBottom: "-1.7in", marginRight: "-2.7in" }}
          >
            <ThemedPostcardFront theme={t} brand={brand} logoUrl={logoUrl} />
          </div>
        </div>
        <div>
          <p className="overline text-[#57534E] mb-4">Postcard · back · 6″ × 4″</p>
          <div
            className="origin-top-left border border-[#E2DCD0]"
            style={{ transform: "scale(0.55)", transformOrigin: "top left", width: "6in", height: "4in", marginBottom: "-1.7in", marginRight: "-2.7in" }}
          >
            <ThemedPostcardBack theme={t} brand={brand} phone={phone} addr1={addr1} addr2={addr2} />
          </div>
        </div>
      </div>

      {/* Social + Decal */}
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <p className="overline text-[#57534E] mb-4">Social post · 1080 × 1080</p>
          <div
            className="origin-top-left border border-[#E2DCD0]"
            style={{ transform: "scale(0.6)", transformOrigin: "top left", width: "5.4in", height: "5.4in", marginBottom: "-2.1in", marginRight: "-2.1in" }}
          >
            <ThemedSocialPost theme={t} brand={brand} logoUrl={logoUrl} />
          </div>
        </div>
        <div>
          <p className="overline text-[#57534E] mb-4">Window decal · 6″ × 6″</p>
          <div
            className="origin-top-left"
            style={{ transform: "scale(0.6)", transformOrigin: "top left", width: "6in", height: "6in", marginBottom: "-2.4in", marginRight: "-2.4in" }}
          >
            <ThemedWindowDecal theme={t} brand={brand} logoUrl={logoUrl} />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function MarketingCompare() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";
  const tagline = "a quiet space to let go.";
  const logoUrl = site?.brand_logo_url || "";
  const phone = site?.phone || "203-437-8193";
  const email = site?.email || "Letgowellness1@gmail.com";
  const addr1 = site?.address_line1 || "808 Highland Ave, 1st Fl";
  const addr2 = site?.address_line2 || "Waterbury, CT 06708";
  const services = site?.services || [];

  const themeOrder = ["imprint"];

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="compare-page">
      <header className="bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-[#E2DCD0] sticky top-0 z-40 print:hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/marketing" data-testid="compare-back" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to kit
            </Link>
            <span className="hidden sm:inline text-[#E2DCD0]">·</span>
            <div>
              <p className="overline text-[#9F5C45]">Compare brand worlds</p>
              <p className="font-serif text-lg text-[#1C1917] leading-none mt-1">Five complete identities · pick one</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/marketing/review" className="inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-[0.18em] uppercase">
              Full kit <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 btn-primary text-xs tracking-[0.18em] uppercase">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-12 print:hidden">
        <p className="overline text-[#9F5C45] mb-4 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Brand identity studio
        </p>
        <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[#1C1917] max-w-4xl">
          Each style is a <em className="italic text-[#3A5A40]">complete world</em>.
        </h1>
        <p className="text-[#57534E] mt-6 max-w-2xl text-base md:text-lg leading-relaxed">
          Cards, letterhead, social posts, postcards — every piece designed in the same key. Scroll through the five worlds.
          Pick the one that feels like you and I&apos;ll finalize the print files.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {themeOrder.map((k) => (
            <a key={k} href={`#${k}`} className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">
              {THEMES[k].name}
            </a>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pb-32">
        {themeOrder.map((k) => (
          <ThemeSection
            key={k}
            themeKey={k}
            brand={brand}
            tagline={tagline}
            logoUrl={logoUrl}
            phone={phone}
            email={email}
            addr1={addr1}
            addr2={addr2}
            services={services}
          />
        ))}

        <div className="border-t border-[#E2DCD0] pt-16 text-center">
          <p className="overline text-[#9F5C45] mb-4">Ready to choose?</p>
          <h3 className="font-serif font-light text-3xl md:text-4xl text-[#1C1917] max-w-2xl mx-auto">
            Just say the word — <em>Sage</em>, <em>Midnight</em>, <em>Onyx</em>, <em>Forest</em>, or <em>Imprint</em>.
          </h3>
          <p className="text-[#57534E] mt-3 max-w-xl mx-auto">
            I&apos;ll finalize all marketing files in that one style and remove the rest.
          </p>
        </div>
      </div>
    </main>
  );
}
